import { useState, useEffect } from 'react';
import { Box, Button, Center, Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import MyDiv from './paySlipCard.style';
import { CustomToast } from '../../../../components';
import { useFetch } from '../../../../hooks/useFetch';
import { CalendarIcon } from '@chakra-ui/icons';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

ChartJS.register(ArcElement, Tooltip, Legend);


interface BankAccountDetail {
  bank_Name: string;
  account_Number: string;
}
interface IPayroll {
  payrollId: number;
  basic: number;
  hra: number;
  conveyance_Allowance: number;
  arrear: number;
  epf: number;
  esi_Health_insurance: number;
  loan_Recovery: number;
  tds: number;
  otherEarning: number;
  otherDeduction: number;
  totalEarnings: number;
  totalDeductions: number;
  netSalary: number;
  incentive: number;
  absentDays: number;
  halfDays: number;
  paidDays: number;
  lop: number;
  entitled: number;
  totalDaysInMonth: number;
  payrollMonth: string;
  status?: 'pending' | 'generated' | 'updated' | 'released' | 'hold';
  comment?: string;
}
export interface ISalarySlip {
  employee_Id: number;
  employeeName: string;
  email: string;
  pan: string;
  uan: string;
  company_Name: string;
  payroll_Company: string;
  employeeCode: string;
  join_Date: string;
  designation: string;
  department: string;
  salary: number;
  bankAccountDetail: BankAccountDetail;
  companyLogoBase64: string;
  payroll: IPayroll;
  payrollStatus: 'pending' | 'generated' | 'updated' | 'released' | 'hold';
}

const PaySlipCard = () => {
  let date = new Date();
  date.setMonth(date.getMonth() - 1);
  const [selectedDate, setSelectedDate] = useState(date);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showSalary, setShowSalary] = useState(false);
  const [payrollData, setPayrollData] = useState<ISalarySlip | null>(null);

  const formatMonthYear = (date: Date) =>
    date.toLocaleString('default', { month: 'long', year: 'numeric' });

  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);

  useEffect(() => {
    loadPaySlip(selectedDate.getFullYear(), selectedDate.getMonth() + 1);
  }, [selectedDate]);

  const loadPaySlip = async (year: number, month: number) => {
    const data = await fetchApi<ISalarySlip>('Payroll/my-payslip', "GET", null, `month=${month}&year=${year}`);
    if (data && data.payrollStatus !== 'pending') setPayrollData(data);
    else setPayrollData(null);
  };

  const downloadPaySlip = async () => {
    const response = await fetchApi(
      'Payroll/download-my-payslip',
      "GET",
      null,
      `month=${selectedDate.getMonth() + 1}&year=${selectedDate.getFullYear()}`,
      undefined,
      undefined,
      true
    );

    if (!response) return;

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Payslip-${selectedDate.getFullYear()}-${String(
      selectedDate.getMonth() + 1
    ).padStart(2, "0")}.pdf`;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const counts = payrollData
    ? [payrollData.payroll.totalEarnings, payrollData.payroll.totalDeductions, payrollData.payroll.netSalary]
    : [0, 0, 0];

  const hasData = counts.some(val => val > 0);

  const chartData = hasData
    ? {
      labels: ['Gross Pay', 'Net Deduction', 'Net Pay'],
      datasets: [
        {
          data: counts,
          backgroundColor: ['#059F58', '#5AC292', '#006838'],
          borderWidth: 0,
          cutout: '70%',
        },
      ],
    }
    : {
      labels: ['No Data'],
      datasets: [
        {
          data: [1],
          backgroundColor: ['#D3D3D3'],
          borderWidth: 0,
          cutout: '70%',
        },
      ],
    };

  const noDataPlugin = {
    id: 'noDataPlugin',
    beforeDraw: (chart: any) => {
      if (hasData) return;
      const { width, height, ctx } = chart;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '10px Poppins';
      ctx.fillStyle = 'black';
      ctx.fillText('No Data Available', width / 2, height / 2);
      ctx.restore();
    },
  };

  /* const chartData = {
    labels: ['Gross Pay', 'Net Deduction', 'Net Pay'],
    datasets: [
      {
        data: salaryData
          ? [salaryData.grossPay, salaryData.netDeduction, salaryData.netPay]
          : [0, 0, 0],
        backgroundColor: ['#059F58', '#5AC292', '#006838'],
        borderWidth: 0,
        cutout: '70%',
      },
    ],
  }; */

  const legendItems = [
    { name: 'Gross Pay', color: '#059F58', value: payrollData?.payroll.totalEarnings ?? 0 },
    { name: 'Net Deduction', color: '#5AC292', value: payrollData?.payroll.totalDeductions ?? 0 },
    { name: 'Net Pay', color: '#006838', value: payrollData?.payroll.netSalary ?? 0 },
  ];

  const options = {
    plugins: {
      legend: { display: false },
      tooltip: { enabled: hasData },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <MyDiv>
      <Box className='common_bg chart-container' padding={2} borderRadius={8}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text className="font-poppins text_md text_semibold">Payslip</Text>
          <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end">
            <PopoverTrigger>
              <Button leftIcon={<CalendarIcon />} padding={3} variant="outline">
                <Text className="font-poppins text_sm text_regular">
                  {formatMonthYear(selectedDate)}
                </Text>
              </Button>
            </PopoverTrigger>
            <PopoverContent w="auto">
              <PopoverBody p={2} display="flex" justifyContent="center">
                <Calendar
                  value={selectedDate}
                  onChange={(value) => {
                    if (value instanceof Date) {
                      setSelectedDate(value);
                      onClose();
                    }
                  }}
                  view="year"
                  maxDetail="year"
                  maxDate={new Date()}
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Flex>

        {loading ?
          <Center h="220px">
            <Spinner size="lg" color="blue.500" />
          </Center>
          :
          <>
            < Box className="common_bg">
              <Doughnut data={chartData} options={options} plugins={[noDataPlugin]} />
            </Box>
            {payrollData ?
              <Box>
                {legendItems.map((item) => (
                  <Flex key={item.name} justify="space-between" align="center" mb={2}>
                    <Flex align="center">
                      <Box w="10px" h="10px" borderRadius="full" bg={item.color} mr={2} />
                      <Text className="legendText font-poppins text_sm text_medium">{item.name}</Text>
                    </Flex>
                    <Text className="legendText common_color font-poppins text_sm text_medium">
                      {showSalary ? `₹${item.value.toLocaleString()}` : '••••••'}
                    </Text>
                  </Flex>
                ))}
              </Box>
              : <Text mt={2} textAlign={'center'} className="legendText font-poppins text_sm text_medium">Payroll not generated!</Text>}
          </>}

        {payrollData && (
          <Flex gap={6} mt={2} justifyContent={"space-between"} mb={2}>
            <Text className="font-poppins text_md text_medium common_color" cursor="pointer" onClick={() => {
              loading ? addToast({ message: 'Wait before trying again!', status: 'info' }) : downloadPaySlip()
            }}>
              Download Slip
            </Text>
            <Text className="font-poppins text_md text_medium common_color" cursor="pointer" onClick={() => setShowSalary(!showSalary)}>
              {showSalary ? 'Hide Salary' : 'Show Salary'}
            </Text>
          </Flex>
        )}
      </Box >
    </MyDiv >
  );
};

export default PaySlipCard;
